<template>
  <v-card class="py-4 pt-10">
    <div class="compare-checkbox">
      <div class="d-flex flex-row justify-end align-center">
        <label
          :for="checkboxId"
          class="d-flex flex-row align-center"
          style="cursor: pointer"
        >
          <div class="text-caption pr-2">COMPARE</div>
          <v-checkbox
            :id="checkboxId"
            :model-value="isSelectedLocal"
            hide-details
            density="compact"
            color="primary"
            class="bordered-checkbox no-margin"
            @update:model-value="handleSelection"
          ></v-checkbox>
        </label>
      </div>
    </div>

    <!-- Header with Image and Compare button -->
    <div class="content-container d-flex flex-column">
      <div class="flex-grow-1">
        <div class="d-flex flex-row align-start">
          <div class="d-flex flex-column">
            <div
              class="report-image-container mr-4"
              :style="coverImageStyle"
              ref="coverImageContainer"
            />
          </div>
          <div
            class="d-flex flex-column justify-space-between"
            style="min-height: 112px"
          >
            <div
              class="text-h6 font-weight-bold mb-4 text-secondary report-title"
            >
              {{ report.title }}
            </div>
            <div class="metric-wrapper d-flex flex-row">
              <div class="d-flex flex-column mr-3">
                <img
                  :src="getFormatIconPath(report.format?.type)"
                  :alt="report.format?.type || 'Format icon'"
                  width="27"
                  height="27"
                />
              </div>
              <div class="d-flex flex-column">
                <div class="text-caption">
                  {{ report.format?.type || 'N/A' }}:
                </div>
                <div class="text-body-2 text-secondary">
                  {{ report.format?.subtype || 'N/A' }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Info Grid -->
        <v-container class="py-4 px-0">
          <v-row no-gutters align-content="space-between">
            <v-col cols="3">
              <div class="text-caption">SUBJECT</div>
              <div
                class="text-body-2 text-secondary font-weight-bold"
                @click.stop="glossaryStore.openGlossary('ela')"
              >
                {{ report.subject?.name || 'N/A' }}
              </div>
            </v-col>
            <v-col cols="3">
              <div class="text-caption">GRADES</div>
              <div class="text-body-2 text-secondary font-weight-bold">
                {{ formatGradeDisplay(report.grades) }}
              </div>
            </v-col>
            <v-col cols="3" class="me-auto">
              <div class="text-caption">RELEASED</div>
              <div class="text-body-2 text-secondary font-weight-bold">
                {{
                  report.release
                    ? new Date(report.release * 1000).toLocaleDateString()
                    : 'N/A'
                }}
              </div>
            </v-col>
            <v-col cols="3" class="d-flex flex-column align-end">
              <div class="text-caption">VERSION</div>
              <div class="text-body-2 text-secondary font-weight-bold">
                {{ (report as any).review_tool?.version || 'N/A' }}
              </div>
            </v-col>
          </v-row>
        </v-container>

        <!-- Gateway Status -->
        <v-container class="pa-0">
          <v-row no-gutters class="gateway-status mb-0">
            <v-col cols="6">
              <div
                v-for="(gateway, index) in report.gateways"
                :key="gateway.slug"
                :class="index === report.gateways.length - 1 ? 'mb-0' : 'mb-5'"
              >
                <div class="text-caption mb-1">
                  {{ gateway.label || 'N/A' }}
                </div>
                <div class="d-flex flex-row align-center">
                  <div
                    :class="
                      gateway.status?.slug === 'meets'
                        ? 'status-icon-green'
                        : gateway.status?.slug === 'does-not-meet'
                          ? 'status-icon-red'
                          : gateway.status?.slug === 'partially-meets'
                            ? 'status-icon-orange'
                            : 'status-icon-gray'
                    "
                  />
                  <div class="text-body-2">
                    {{ gateway.status?.name || 'N/A' }}
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="6" class="pl-4">
              <div
                v-for="(detail, index) in report.details"
                :key="detail.label"
                :class="index === report.details.length - 1 ? 'mb-0' : 'mb-5'"
              >
                <div class="text-caption mb-1">
                  {{ detail.label?.replace(/<[^>]*>/g, '') || '' }}
                </div>
                <div class="d-flex flex-row align-center">
                  <div class="text-body-2 text-secondary mb-4">
                    {{ detail.value || 'N/A' }}
                  </div>
                </div>
                <div class="text-caption mb-1">
                  Multilingual Learner Supports:
                </div>
                <div class="d-flex flex-row align-center">
                  <div class="text-body-2 text-secondary">
                    {{ detail.value || 'N/A' }}
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
          <template v-if="report.alerts?.length">
            <v-row class="mt-0">
              <v-col cols="12" class="py-2">
                <v-alert
                  :text="
                    report.alerts[0]?.content?.replace(/<[^>]*>/g, '') || ''
                  "
                  type="warning"
                  variant="tonal"
                  class="text-uppercase"
                  :icon="false"
                  density="compact"
                  style="font-size: 10px; font-weight: bold"
                ></v-alert>
              </v-col>
            </v-row>
          </template>
        </v-container>
      </div>

      <v-container class="pa-0 mt-auto">
        <v-row>
          <v-col>
            <div
              class="copyright text-secondary py-4"
              v-html="report.copyright || ''"
            ></div>
            <v-btn
              block
              variant="outlined"
              color="lighter-grey"
              append-icon="mdi-arrow-right"
              @click="viewFullReport"
            >
              <span class="text-secondary">View Full Report</span>
              <template v-slot:append>
                <v-icon color="secondary"> </v-icon>
              </template>
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </v-card>
</template>

<script setup lang="ts">
  import type { Report } from '../../store';
  import { useGlossaryStore, useCompareStore } from '../../store';
  import { useRouter } from 'vue-router';
  import { formatGradeDisplay } from '../../lib/grades';
  import {
    computed,
    ref,
    onMounted,
    watch,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
  } from 'vue';

  const props = defineProps<{
    report: Report;
    instanceId: string;
  }>();

  const glossaryStore = useGlossaryStore();
  const compareStore = useCompareStore();
  const router = useRouter();
  const coverImageLoaded = ref(true);
  const fallbackCoverUrl = ref('');
  const instance = getCurrentInstance();
  const isPriority = ref(false); // Track if card is priority (first 3 cards)

  // Pre-compute values to reduce calculation during interactions
  const isSelectedLocal = ref(
    props.instanceId
      ? compareStore.selectedReports.some(
          (item) => item.id === props.instanceId,
        )
      : false,
  );

  const checkboxId = `compare-checkbox-${props.instanceId}`;

  // Use a low quality placeholder image for immediate display if needed
  const placeholderImage =
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzMDAgNDAwIiB3aWR0aD0iNzIiIGhlaWdodD0iOTUiPjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjZWJmN2ZiIi8+PC9zdmc+';

  // Memoize this computation to improve performance
  const coverImageStyle = computed(() => {
    if (!coverImageLoaded.value && fallbackCoverUrl.value) {
      return {
        backgroundImage: `url(${fallbackCoverUrl.value})`,
      };
    }

    // Skip low quality preview for LCP improvements
    return {
      backgroundImage: props.report.cover?.uri
        ? `url(${props.report.cover.uri})`
        : `url(${placeholderImage})`,
      backgroundSize: 'cover',
    };
  });

  // Cache format icons to avoid repeated URL creation
  const formatIconCache = new Map();

  /**
   * Maps format types to local SVG files with caching for better performance
   */
  function getFormatIconPath(formatType: string | undefined): string {
    if (!formatType) {
      return (
        formatIconCache.get('default') ||
        (formatIconCache.set(
          'default',
          new URL('@/assets/icon-format-1.svg', import.meta.url).href,
        ) &&
          formatIconCache.get('default'))
      );
    }

    const key = formatType.toLowerCase();
    if (formatIconCache.has(key)) {
      return formatIconCache.get(key);
    }

    let iconUrl;
    // Map format types to specific icons (add more as needed)
    switch (key) {
      case 'core':
        iconUrl = new URL('@/assets/icon-format-1.svg', import.meta.url).href;
        break;
      case 'supplement':
        iconUrl = new URL('@/assets/icon-format-2.svg', import.meta.url).href;
        break;
      case 'support':
        iconUrl = new URL('@/assets/icon-format-3.svg', import.meta.url).href;
        break;
      default:
        iconUrl = new URL('@/assets/icon-format-1.svg', import.meta.url).href;
    }

    formatIconCache.set(key, iconUrl);
    return iconUrl;
  }

  // Optimize selection handling for better INP
  function handleSelection(value: boolean | null) {
    // Update local state immediately for responsive UI
    const selected = !!value;

    // Immediately reflect UI state
    isSelectedLocal.value = selected && compareStore.selectedReports.length < 3;

    // Use queueMicrotask for better performance than nextTick in this case
    window.queueMicrotask(() => {
      if (selected) {
        if (compareStore.selectedReports.length < 3) {
          compareStore.addReport(props.instanceId, props.report);
        }
      } else {
        compareStore.removeReport(props.instanceId);
      }
    });
  }

  // Optimize navigation to avoid jank and improve INP
  const viewFullReport = () => {
    // Skip navigation if already navigating
    if (
      router.currentRoute.value.name === 'report-overview' &&
      router.currentRoute.value.params.id === props.report.slug
    ) {
      return;
    }

    // Navigate directly without animation frames
    router.push({
      name: 'report-overview',
      params: { id: props.report.slug },
    });
  };

  function validateCoverImage() {
    // If there's no cover image, use placeholder
    if (!props.report.cover?.uri) {
      coverImageLoaded.value = false;
      fallbackCoverUrl.value = placeholderImage;
      return;
    }

    // Preload the actual image with high priority for LCP elements
    const img = new Image();

    // Set loading priority based on card position
    if ('fetchPriority' in img) {
      (img as any).fetchPriority = isPriority.value ? 'high' : 'low';
    }

    img.onload = () => {
      coverImageLoaded.value = true;
    };

    img.onerror = () => {
      // Fall back to placeholder on error
      coverImageLoaded.value = false;
      fallbackCoverUrl.value = placeholderImage;
    };

    // Load the image immediately for LCP improvement - remove setTimeout
    img.src = props.report.cover?.uri || '';
  }

  // Improve image loading for LCP
  function optimizeLCPForPriorityCards(cardElement: HTMLElement) {
    // Check if this is one of the first 3 cards (priority for LCP)
    if (cardElement.closest('.priority')) {
      isPriority.value = true;

      // Find image in this card
      const reportImage = cardElement.querySelector('.report-image-container');
      if (reportImage) {
        reportImage.setAttribute('data-lcp-priority', 'true');
      }
    }
  }

  onMounted(() => {
    // Optimize LCP by checking if this is a priority card
    if (instance && instance.vnode.el) {
      optimizeLCPForPriorityCards(instance.vnode.el as HTMLElement);
    }

    validateCoverImage();

    // Add passive event listeners to improve touch response
    if (instance && instance.vnode.el) {
      const el = instance.vnode.el as HTMLElement;
      const touchTargets = el.querySelectorAll(
        'button, a, .v-checkbox, [role="button"]',
      );

      touchTargets.forEach((target) => {
        target.addEventListener('touchstart', () => {}, { passive: true });
      });
    }
  });

  // Optimize watch to reduce unnecessary processing
  watch(
    () => compareStore.selectedReports,
    (newSelection) => {
      // Compute this value only when needed
      const shouldBeSelected = newSelection.some(
        (item) => item.id === props.instanceId,
      );

      // Only update local state if it differs from store state
      if (isSelectedLocal.value !== shouldBeSelected) {
        // Use requestAnimationFrame to schedule UI updates after paint
        requestAnimationFrame(() => {
          isSelectedLocal.value = shouldBeSelected;
        });
      }
    },
    { deep: true },
  );
</script>

<style lang="scss" scoped>
  // Variables
  $image-width: 72px;
  $image-height: 95px;
  $font-size-small: 10px;
  $font-size-base: 12px;
  $font-size-large: 28px;
  $spacing-base: 16px;
  $spacing-large: 24px;
  $border-radius: 8px;

  // Status color variables - using global variables now

  // Mixins
  @mixin flex-center {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @mixin text-base {
    font-size: $font-size-base;
    color: rgb(var(--v-theme-text-secondary));
  }

  // Status icon mixin - using global mixin now

  // Layout
  .content-container {
    position: relative;
    padding: $spacing-base $spacing-base 0;
    contain: content; /* Improve browser rendering performance */
    content-visibility: auto; /* Enable automatic content visibility for offscreen elements */
  }

  .compare-checkbox {
    position: absolute;
    top: 0;
    right: 0;
    touch-action: manipulation; /* Improve touch responsiveness */
  }

  // Report Image
  .report-image-container {
    display: flex;
    width: $image-width;
    height: $image-height;
    border: solid 1px rgb(var(--v-theme-light-grey));
    background-size: cover;
    background-position: center;
    will-change: transform; /* Hint for browser optimization */
    transform: translateZ(0); /* Force GPU acceleration */
    backface-visibility: hidden; /* Reduce paint complexity */
    contain: paint; /* Optimize paint performance */
  }

  // Report Title
  .report-title {
    font-family: 'rockwell', sans-serif;
    font-size: $font-size-large !important;
    font-weight: normal !important;
  }

  // Metric Wrapper
  .metric-wrapper {
    .text-caption {
      text-transform: uppercase !important;
    }
  }

  // Gateway Status
  .gateway-status {
    background-color: rgba(var(--v-theme-blue), 0.05);
    border-radius: $border-radius;
    padding: $spacing-large $spacing-base;
    contain: content; /* Improve rendering performance */

    .text-body-2 {
      display: inline-block;
    }

    .text-caption {
      text-transform: uppercase !important;
    }
  }

  // Copyright
  .copyright {
    font-size: $font-size-small !important;
    font-family: 'proxima-nova', sans-serif !important;
    line-height: 1.2;
    padding: 2px 0 8px 0;
    min-height: 32px;

    :deep(*) {
      font-size: $font-size-small !important;
      font-family: 'proxima-nova', sans-serif !important;
      line-height: 1.2;
      margin: 0;
      padding: 0;
    }
  }

  // Button Styles - Optimize for touch
  .v-btn {
    border-width: 2px !important;
    transition: border-color 0.2s ease-in-out;
    touch-action: manipulation; /* Improve touch responsiveness */

    :deep(.v-btn__overlay) {
      display: none;
    }

    :deep(.v-btn__content) {
      .text-secondary {
        color: rgb(var(--v-theme-secondary)) !important;
        font-weight: bold;
        transition: color 0.2s ease-in-out;
      }
    }

    :deep(.v-btn__append) {
      .v-icon {
        transition: color 0.2s ease-in-out;
      }
    }

    &:hover {
      border-color: rgb(var(--v-theme-primary)) !important;
      background-color: rgba(var(--v-theme-primary), 0.05) !important;
      .v-btn__content {
        .text-secondary {
          color: rgb(var(--v-theme-primary)) !important;
        }
      }
      .v-btn__append {
        .v-icon {
          color: rgb(var(--v-theme-primary)) !important;
        }
      }
    }
  }

  // Alert Styles
  .v-alert.text-warning {
    :deep(.v-alert__content) {
      font-size: $font-size-small;
      font-weight: bold;
      color: #02080c !important;
    }
  }
</style>
